import { Button } from "@/components/ui";
import { expandWorkToastForQuestion } from "@/components/toasts/expand-work-toast";
import { useHasWorkToastQuestion } from "@/components/toasts/use-has-work-toast-question";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";

interface AgentCursorQuestionBadgeProps {
  agentName: string;
  awaitingInputId: string;
  testId: string;
  /** Called when no work toast holds this question, so the click can
   *  refocus the owning chat instead. */
  onExpandMissed?: () => void;
}

/** Cursor name tag for a pending question. The question itself renders in
 *  its work toast; the "?" expands that toast on the display this cursor is
 *  drawn on (`null` on the web canvas). */
export function AgentCursorQuestionBadge({
  agentName,
  awaitingInputId,
  testId,
  onExpandMissed,
}: AgentCursorQuestionBadgeProps) {
  const t = useT(cursorsMessages);
  const hasWorkToast = useHasWorkToastQuestion(awaitingInputId);

  // Only show the badge when the click has somewhere to go: a pill holding
  // this question, or a chat to fall back to. A question raised on another
  // device never creates a pill here, and a "?" that does nothing is worse
  // than no badge — that question is answered from the chat inline card or
  // the frame's awaiting-input bar.
  if (!hasWorkToast && !onExpandMissed) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="pointer-events-auto h-6 max-w-[200px] gap-1.5 pl-2 pr-1 text-appkit-mini font-semibold"
      aria-label={t("questionBadgeLabel", { agentName })}
      data-interactive="true"
      data-testid={`${testId}-question-badge`}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        if (expandWorkToastForQuestion(awaitingInputId, getWindowDisplayId())) {
          return;
        }
        // No live pill holds this awaiting-input id — either none was ever
        // created here (a question raised on another device, which the
        // toast adapter drops) or the one that was has since been dismissed
        // or removed between render and click. Refocus the owning chat
        // instead.
        onExpandMissed?.();
      }}
    >
      <span className="min-w-0 truncate">{agentName}</span>
      <span
        aria-hidden="true"
        className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-warning text-appkit-mini font-bold leading-none text-black"
      >
        ?
      </span>
    </Button>
  );
}
